import { Character } from '@cartwhl/common/api/getCharacters';
import { HAND_OPTIONS } from '@cartwhl/common/types';
import { FromStore } from '@cartwhl/common/types';

/**
 * Takes a hand name and returns a pretty name
 * @param handName - The name of the hand
 * @returns The pretty name of the hand
 */
export function getHandDisplayName(handName: string) {
  // take the hand name and replace the underscores with spaces
  // and capitalize the first letter of the string
  if (handName === HAND_OPTIONS.DEFAULT) {
    return 'Animated';
  }
  return handName.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/**
 * Get display name for face expression (capitalize first letter, add spaces between words)
 * @param face - The face expression
 * @returns The display name of the face expression
 */
export const getFaceDisplayName = (face: string): string => {
  // Handle special cases like "DelightedGrin", "HappyLookLeft", etc.
  if (face.includes('Look')) {
    return face.replace('Look', ' Look ');
  }

  if (face === 'DelightedGrin') {
    return 'Delighted Grin';
  }

  if (face === 'SadEyesClosed') {
    return 'Sad Eyes Closed';
  }

  // Add spaces before capital letters and capitalize first letter
  return face
    .replace(/([A-Z])/g, ' $1')
    .replace(/^\s+/, '')
    .replace(/^\w/, (c) => c.toUpperCase());
};

/**
 * Checks if a character is a free character
 * @param c - The character to check
 * @returns True if the character is a free character, false otherwise
 */
export const freeChars = (c: FromStore<Character>) =>
  c.createdBy === 'FREE_CHARACTERS';

/**
 * Checks if a character is an uploaded character
 */
export const uploadedChars = (c: FromStore<Character>) =>
  c.createdBy !== 'FREE_CHARACTERS';

/**
 * Sorts characters by creation date
 * @param a - The first character
 * @param b - The second character
 * @returns -1 if the first character is older, 1 if the second character is older, 0 if they are the same
 */
export const latest = (a: FromStore<Character>, b: FromStore<Character>) => {
  const dateA = new Date(a.createdAt);
  const dateB = new Date(b.createdAt);
  if (dateA > dateB) return -1;
  if (dateA < dateB) return 1;
  return 0;
};

/**
 * Sorts characters by name
 * @param a - The first character
 * @param b - The second character
 * @returns -1 if the first character is alphabetically before the second, 1 if it is after, 0 if they are the same
 */
export const byName = (a: FromStore<Character>, b: FromStore<Character>) => {
  if (a.characterName < b.characterName) return -1;
  if (a.characterName > b.characterName) return 1;
  return 0;
};

/**
 * Checks if a character has a face
 * @param c - The character to check
 * @returns True if the character has a face, false otherwise
 */
export const hasFace = (c?: FromStore<Character>) => {
  const isFree = c?.createdBy === 'FREE_CHARACTERS';
  const isAxel = c?.characterName === 'Axel';
  return isFree && !isAxel;
};

/**
 * Checks if a character has a skin tone
 * @param c - The character to check
 * @returns True if the character has a skin tone, false otherwise
 */
export const hasSkinTone = (c?: FromStore<Character>) => {
  const isFree = c?.createdBy === 'FREE_CHARACTERS';
  const isAxel = c?.characterName === 'Axel';
  return isFree && !isAxel;
};
